// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance from '../../services/axiosInstance';
import { apiCall } from '../utils/utils';

import { uploadImageAndRefresh } from '../utils/imageCache';

import {
  getShowEquipUuid,
  LIGHT_EQUIPMENT_TYPE_ID,
  LIGHTING_PRESET_BLK_ESET_ID,
} from '../../utils/sceneEquipmentsConfig';

import { fetchShowScripts } from '../scripts/scriptsActions';

import moStudio from '../../assets/image/default.png';

// fetch all shows from a company
export const fetchCompanyShows = createAsyncThunk(
  'shows/fetchCompanyShows',
  async (companyId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`show/live/company/${companyId}/`), rejectWithValue)
);

// Create a show
export const createShow = createAsyncThunk(
  'shows/createShow',
  async ({ studioUuid, data }, { dispatch, rejectWithValue }) => {
    try {
      const newShow = await apiCall(() => axiosInstance.post(`show/`, data), rejectWithValue);
      const showUuid = newShow?.show_uuid;
      if (!showUuid) {
        throw new Error('Failed to create show');
      }

      // Copy equipment
      await dispatch(
        copyStudioEquipToShow({
          show_uuid: showUuid,
          studio_uuid: studioUuid,
        })
      ).unwrap();
      // add default image to show
      await dispatch(
        updateShowImage({
          showId: showUuid,
          lastUpdated: newShow.last_updated,
          defaultImage: moStudio,
        })
      );
      return newShow;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create show.');
    }
  }
);

// duplicate a show
export const duplicateShow = createAsyncThunk(
  'shows/duplicateShow',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`show/copy/`, data), rejectWithValue)
);

// update show
export const updateShow = createAsyncThunk(
  'shows/updateShow',
  async ({ data }, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`show/update/`, data), rejectWithValue)
);

// Delete a show
export const deleteShow = createAsyncThunk(
  'shows/deleteShow',
  async (showId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`show/delete/${showId}/`), rejectWithValue)
);

// Get Show by Uuid
export const fetchShowById = createAsyncThunk(
  'shows/fetchShowById',
  async (showId, { rejectWithValue }) => {
    try {
      const show = await apiCall(() => axiosInstance.get(`show/${showId}/`), rejectWithValue);
      if (show?.show_blocks?.length) {
        // If show has blocks sort show.blocks array by block.order
        show.show_blocks = show.show_blocks.sort((a, b) => a.order - b.order);
      }
      return show;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

// Get show equipments
export const fetchShowEquipments = createAsyncThunk(
  'shows/fetchShowEquipments',
  async ({ showId }, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`show_equipment/${showId}/all/`), rejectWithValue)
);

// copy studio equipments to show
export const copyStudioEquipToShow = createAsyncThunk(
  'shows/copyStudioEquipToShow',
  async (data, { rejectWithValue }) => {
    try {
      if (!data?.studio_uuid) {
        throw new Error(`A valid studio must be provided.`);
      }
      return apiCall(
        () => axiosInstance.post(`show_equipment/copy_studio/`, data),
        rejectWithValue
      );
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

// create show equipment
export const createShowEquipment = createAsyncThunk(
  'shows/createShowEquipment',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`show_equipment/`, data), rejectWithValue)
);

// get show blocks by show uuid
export const fetchShowBlocks = createAsyncThunk(
  'shows/fetchShowBlocks',
  async (showId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`show_block/all/show/${showId}/`), rejectWithValue)
);

// Get all scenes from a show
export const fetchShowScenes = createAsyncThunk(
  'shows/fetchShowScenes',
  async (showId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`show_scene/all/${showId}/`), rejectWithValue)
);

// Add a scene to a show
export const addSceneToShow = createAsyncThunk(
  'shows/addSceneToShow',
  async (data, { rejectWithValue }) =>
    await apiCall(() => axiosInstance.post(`show_scene/`, data), rejectWithValue)
);

// delete a scene from a show
export const deleteSceneFromShow = createAsyncThunk(
  'shows/deleteSceneFromShow',
  async (showSceneUuid, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`show_scene/delete/${showSceneUuid}/`), rejectWithValue)
);

// Create a block
export const createBlock = createAsyncThunk(
  'shows/createBlock',
  async ({ show, data }, { dispatch, rejectWithValue }) => {
    try {
      const newBlock = await apiCall(
        () => axiosInstance.post(`show_block/`, data),
        rejectWithValue
      );
      if (newBlock?.block_uuid) {
        // add default image to block
        await dispatch(
          updateBlockImage({
            blockId: newBlock.block_uuid,
            defaultImage: moStudio,
          })
        );
        // add lighting equipment to the new block
        const targetEquip = { equipmentUuid: LIGHT_EQUIPMENT_TYPE_ID };
        const showEquipUuid = getShowEquipUuid(show, targetEquip);
        if (showEquipUuid) {
          await dispatch(
            addBlockEquipment({
              block_uuid: newBlock.block_uuid,
              show_equip_uuid: showEquipUuid,
              equipment_settings: [
                {
                  equip_setting_uuid: LIGHTING_PRESET_BLK_ESET_ID,
                  value: '0', // 0 "Blackout" default value
                },
              ],
            })
          ).unwrap();
        }
      }
      return newBlock;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create block.');
    }
  }
);

// duplicate a block
export const duplicateBlock = createAsyncThunk(
  'shows/duplicateBlock',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`show_block/copy/`, data), rejectWithValue)
);

/**
 * Duplicate a block, refresh scripts and blocks, then return the new
 * block_uuid for navigation.
 */
export const duplicateBlockAndNavigate = createAsyncThunk(
  'shows/duplicateBlockAndNavigate',
  async ({ block, itemBefore, showId }, { dispatch, rejectWithValue }) => {
    try {
      const action = await dispatch(
        duplicateBlock({
          ...block,
          name: `${block.name} copy`,
          insert_after: itemBefore ? itemBefore.block_uuid : block.block_uuid,
        })
      );

      if (!duplicateBlock.fulfilled.match(action)) {
        return rejectWithValue(action.payload || 'Failed to duplicate block');
      }

      await dispatch(fetchShowScripts(showId));
      await dispatch(fetchShowBlocks(showId));

      return { blockId: action.payload?.block_uuid ?? null };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to duplicate block');
    }
  }
);

// get block by id
export const fetchBlockById = createAsyncThunk(
  'shows/fetchBlockById',
  async (blockId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`show_block/${blockId}/`), rejectWithValue)
);

// get block by id
export const fetchBlockScripts = createAsyncThunk(
  'shows/fetchBlockScripts',
  async ({ showId, blockId }, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`script/${showId}/block/${blockId}/`), rejectWithValue)
);

// update show image
export const updateShowImage = createAsyncThunk(
  'shows/updateShowImage',
  async (
    { showId, blockId = null, imgType = 'Large', lastUpdated = '', defaultImage = '' },
    { rejectWithValue }
  ) => {
    try {
      const blockPrefix = 'show_block';
      const showPrefix = 'show';
      const result = await uploadImageAndRefresh({
        id: showId,
        sourceId: blockId,
        imgType,
        endpointPrefix: showPrefix,
        sourceEndpointPrefix: blockPrefix,
        lastUpdated,
        defaultImage,
      });
      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail?.[0]?.msg || error?.message || 'Failed to update show image'
      );
    }
  }
);

// create or update show_block image
export const updateBlockImage = createAsyncThunk(
  'shows/updateBlockImage',
  async (
    { blockId, sceneId = null, imgType = 'Large', lastUpdated = '', defaultImage = '' },
    { rejectWithValue }
  ) => {
    try {
      const scenePrefix = 'scene';
      const blockPrefix = 'show_block';
      const result = await uploadImageAndRefresh({
        id: blockId,
        sourceId: sceneId,
        imgType,
        endpointPrefix: blockPrefix,
        sourceEndpointPrefix: scenePrefix,
        lastUpdated,
        defaultImage,
      });
      return result;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.detail?.[0]?.msg || error?.message || 'Failed to update block image'
      );
    }
  }
);

// Helper function to update the block_image and update the show image if needed
export const uploadAndFetchImage = createAsyncThunk(
  'shows/uploadAndFetchImage',
  async (
    { blockId, sceneId, base64Image = null, imgType = 'Thumbnail', lastUpdated = '' },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      await dispatch(
        updateBlockImage({
          blockId,
          sceneId,
          imgType: 'Large',
          lastUpdated,
          defaultImage: base64Image,
        })
      );

      // if first block in show update show image
      const state = getState();
      const show = state.shows.show;

      const updatedBlock = show.show_blocks.find(b => b.block_uuid === blockId);
      const isFirstBlock =
        show?.show_blocks?.length &&
        updatedBlock?.order === Math.min(...show.show_blocks.map(b => b.order));

      if (isFirstBlock) {
        await dispatch(
          updateShowImage({
            showId: show.show_uuid,
            blockId,
            imgType: 'Large',
            lastUpdated: show.last_updated,
            defaultImage: base64Image,
          })
        );
      }
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to upload and fetch image');
    }
  }
);

// update a block
export const updateBlock = createAsyncThunk(
  'shows/updateBlock',
  async ({ data }, { rejectWithValue }) =>
    apiCall(() => axiosInstance.patch(`show_block/update/`, data), rejectWithValue)
);

/**
 * Persist a block reorder: updateBlock with the pre-computed insert position,
 * refetch the canonical block list, and conditionally update the show image.
 *
 * The optimistic reorderShowBlocks dispatch stays in the component so the UI
 * updates immediately before the network round-trip completes.
 */
export const persistBlockReorder = createAsyncThunk(
  'shows/persistBlockReorder',
  async (
    { movedItem, newList, showBlocks, showId, show, insertPosition },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const data = { ...movedItem, ...insertPosition };
      const response = await dispatch(updateBlock({ data }));

      if (!updateBlock.fulfilled.match(response)) {
        return rejectWithValue(response.payload || 'Failed to reorder block');
      }

      await dispatch(fetchShowBlocks(showId));

      // Update show image if the first block changed
      if (showBlocks[0]?.block_uuid !== newList[0]?.block_uuid) {
        await dispatch(
          updateShowImage({
            showId,
            blockId: newList[0]?.block_uuid,
            lastUpdated: show?.last_updated,
          })
        );
      }

      return { success: true };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to reorder block');
    }
  }
);

// Delete a show_block
export const deleteBlock = createAsyncThunk(
  'shows/deleteBlock',
  async (blockId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.delete(`show_block/delete/${blockId}`), rejectWithValue)
);

/**
 * Delete a block and conditionally update the show image.
 * Returns { nextBlockId, prevBlockId } so the component can navigate.
 */
export const deleteBlockWithNavigation = createAsyncThunk(
  'shows/deleteBlockWithNavigation',
  async ({ block, showBlocks, showId, show }, { dispatch, rejectWithValue }) => {
    try {
      const currentIndex = showBlocks.findIndex(b => b.block_uuid === block.block_uuid);
      const nextBlock = showBlocks[currentIndex + 1] || null;
      const prevBlock = showBlocks[currentIndex - 1] || null;

      const action = await dispatch(deleteBlock(block.block_uuid));
      if (!deleteBlock.fulfilled.match(action)) {
        return rejectWithValue(action.payload || 'Failed to delete block');
      }

      // If the first block is deleted, update the show image
      if (currentIndex === 0) {
        await dispatch(
          updateShowImage({
            showId,
            blockId: nextBlock ? nextBlock.block_uuid : null,
            lastUpdated: show?.last_updated,
            defaultImage: !nextBlock && !prevBlock ? moStudio : null,
          })
        );
      }

      return {
        nextBlockId: nextBlock?.block_uuid ?? null,
        prevBlockId: prevBlock?.block_uuid ?? null,
      };
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to delete block');
    }
  }
);

// get all equipments in a block
export const fetchBlockEquipments = createAsyncThunk(
  'shows/fetchBlockEquipments',
  async (blockId, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`show_block/${blockId}/equipment/`), rejectWithValue)
);

// add block equipment to a block
export const addBlockEquipment = createAsyncThunk(
  'shows/addBlockEquipment',
  async (data, { rejectWithValue }) => {
    try {
      const { show_equip_uuid: showEquipUuid } = data;
      if (!showEquipUuid) {
        throw new Error('Show equipment not found');
      }
      return apiCall(() => axiosInstance.post(`show_block/add/equipment`, data), rejectWithValue);
    } catch (error) {
      return rejectWithValue(error.message || 'failed to add equipment');
    }
  }
);

// delete equipment from block
export const deleteBlockEquipment = createAsyncThunk(
  'shows/deleteBlockEquipment',
  async (blockEquipmentId, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`show_block/remove/equipment/${blockEquipmentId}/`),
      rejectWithValue
    )
);

// Create block equipment setting value
export const addBlockEquipmentSettingValue = createAsyncThunk(
  'shows/addBlockEquipmentSettingValue',
  async ({ blockEquipId, data }, { rejectWithValue }) =>
    await apiCall(
      () => axiosInstance.post(`block_equipment/${blockEquipId}/setting/`, data),
      rejectWithValue
    )
);

// update block equipment setting value
export const updateBlockEquipmentSettingValue = createAsyncThunk(
  'shows/updateBlockEquipmentSettingValue',
  async ({ blockEquipId, data }, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.patch(`block_equipment/${blockEquipId}/setting/update/`, data),
      rejectWithValue
    )
);

// delete equipment from block
export const deleteBlockEquipmentSettingValue = createAsyncThunk(
  'shows/deleteBlockEquipmentSettingValue',
  async ({ blockEquipId, blockEsetValId }, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.delete(`block_equipment/${blockEquipId}/setting/${blockEsetValId}/`),
      rejectWithValue
    )
);

// add show member
export const addShowMember = createAsyncThunk(
  'shows/addShowMember',
  async (data, { rejectWithValue }) => {
    return await apiCall(() => axiosInstance.post(`show/add/member/`, data), rejectWithValue);
  }
);

// remove show member
export const removeShowMember = createAsyncThunk(
  'shows/removeShowMember',
  async ({ showAccessUuid }, { rejectWithValue }) =>
    await apiCall(
      () => axiosInstance.delete(`show/remove/member/${showAccessUuid}/`),
      rejectWithValue
    )
);

// remove show member
export const allShowMembers = createAsyncThunk(
  'shows/allShowMembers',
  async ({ showId }, { rejectWithValue }) =>
    await apiCall(() => axiosInstance.get(`show/member/all/${showId}/`), rejectWithValue)
);

// SHOW DEPLOYMENT
// deploy deployment
export const createShowDeploy = createAsyncThunk(
  'shows/createShowDeploy',
  async (data, { rejectWithValue }) =>
    apiCall(() => axiosInstance.post(`deployment/`, data), rejectWithValue)
);

// get show
export const fetchShowDeployUuid = createAsyncThunk(
  'shows/fetchShowDeployUuid',
  async ({ showId, studioUuid }, { rejectWithValue }) =>
    apiCall(() => axiosInstance.get(`/deployment/${showId}/${studioUuid}/`), rejectWithValue)
);

// deploy show
export const deployShow = createAsyncThunk(
  'shows/deployShow',
  async ({ deployId }, { rejectWithValue }) => {
    try {
      if (!deployId) {
        throw new Error('Deployment ID is required');
      }
      return await apiCall(
        () => axiosInstance.get(`deployment/${deployId}/deploy/`),
        rejectWithValue
      );
    } catch (error) {
      return rejectWithValue(error.message || 'failed to deploy show');
    }
  }
);

// Automatically create deployment mapping
export const createDeploymentMapping = createAsyncThunk(
  'shows/createDeploymentMapping',
  async ({ companyUuid, data }, { rejectWithValue }) =>
    apiCall(
      () => axiosInstance.post(`/deployment/automatic/mapping/${companyUuid}/`, data),
      rejectWithValue
    )
);
